import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { Card } from '@/components/ui/Card';

interface ConversationSummary {
  id: string;
  bankId: string;
  bankName: string;
  channel: string;
  contactName: string | null;
  contactEmail: string | null;
  status: string;
  verdict: string | null;
  overrideVerdict: string | null;
  scoreTotal: string;
  confidence: string | null;
  turnCount: number;
  lastActivityAt: string;
  finalizedAt: string | null;
}

const VERDICT_CLASSES: Record<string, string> = {
  QUALIFIED: 'bg-green-900/40 text-green-200',
  DISQUALIFIED: 'bg-red-900/40 text-red-200',
  NEEDS_HUMAN_REVIEW: 'bg-amber-900/40 text-amber-200',
};

const STATUS_CLASSES: Record<string, string> = {
  IN_PROGRESS: 'bg-blue-900/40 text-blue-200',
  WAITING_FOR_USER: 'bg-blue-950/40 text-blue-300',
  WAITING_FOR_CONSENT: 'bg-amber-950/40 text-amber-200',
  COMPLETED: 'bg-green-950/40 text-green-300',
  ABANDONED: 'bg-muted text-muted-foreground',
};

/**
 * Story 106.1 — operator list of inbound qualification conversations.
 * Drilldown on a row opens the transcript review page.
 */
export function QualificationConversationsPage() {
  const [convs, setConvs] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [verdictFilter, setVerdictFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (verdictFilter) params.set('verdict', verdictFilter);
    params.set('limit', '200');
    apiClient
      .get<ConversationSummary[]>(`/qualification/conversations?${params.toString()}`)
      .then(({ data }) => {
        if (!cancelled) setConvs(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [statusFilter, verdictFilter]);

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-semibold">Qualification conversations</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Inbound conversations driven by the qualification agent. Click a
          row to review the transcript, the agent's reasoning per turn,
          and (when needed) override the verdict.
        </p>
      </div>

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <label className="text-xs font-medium">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-md border bg-background px-3 py-2 text-sm"
            >
              <option value="">Any</option>
              <option value="IN_PROGRESS">In progress</option>
              <option value="WAITING_FOR_USER">Waiting for user</option>
              <option value="WAITING_FOR_CONSENT">Waiting for consent</option>
              <option value="COMPLETED">Completed</option>
              <option value="ABANDONED">Abandoned</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Verdict</label>
            <select
              value={verdictFilter}
              onChange={(e) => setVerdictFilter(e.target.value)}
              className="rounded-md border bg-background px-3 py-2 text-sm"
            >
              <option value="">Any</option>
              <option value="QUALIFIED">Qualified</option>
              <option value="DISQUALIFIED">Disqualified</option>
              <option value="NEEDS_HUMAN_REVIEW">Needs human review</option>
            </select>
          </div>
        </div>
      </Card>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : convs.length === 0 ? (
        <Card>
          <p className="text-sm text-muted-foreground">No conversations yet.</p>
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Contact</th>
                <th className="px-4 py-2 text-left">Bank</th>
                <th className="px-4 py-2 text-left">Channel</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Verdict</th>
                <th className="px-4 py-2 text-right">Score</th>
                <th className="px-4 py-2 text-right">Turns</th>
                <th className="px-4 py-2 text-left">Last activity</th>
              </tr>
            </thead>
            <tbody>
              {convs.map((c) => {
                const effectiveVerdict = c.overrideVerdict ?? c.verdict;
                return (
                  <tr key={c.id} className="border-t">
                    <td className="px-4 py-2">
                      <Link
                        to={`/qualification/conversations/${c.id}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {c.contactName ?? c.contactEmail ?? '—'}
                      </Link>
                      {c.contactName && c.contactEmail && (
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {c.contactEmail}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs">{c.bankName}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {c.channel}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          STATUS_CLASSES[c.status] ?? 'bg-muted'
                        }`}
                      >
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      {effectiveVerdict ? (
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            VERDICT_CLASSES[effectiveVerdict] ?? 'bg-muted'
                          }`}
                          title={c.overrideVerdict ? 'Operator override' : 'Agent verdict'}
                        >
                          {effectiveVerdict}
                          {c.overrideVerdict && ' (override)'}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {Number(c.scoreTotal).toFixed(2)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {c.turnCount}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {new Date(c.lastActivityAt).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
